import React, { useEffect, useMemo, useState } from 'react'
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom'
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined'
import { Box, Typography } from '@mui/material'
import { CommonButton, CommonTabs } from '@/components/common'
import {
  AccountDonorsTab,
  DriveAttendeesTab,
  ScheduledAppointmentsTab,
} from '@/components/chairperson/drive-donors'
import { chairpersonService } from '@/services'
import { formatIsoDateWithWeekday } from '@/utils/date'
import { getChairpersonDriveBackTarget } from '@/utils/chairpersonNavigation'

export const ChairpersonDriveDonorsPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { driveId } = useParams()
  const { selectedAccountId } = useOutletContext()
  const accountId = location.state?.accountId ?? selectedAccountId
  const driveName = location.state?.driveName
  const fromTab = location.state?.fromTab ?? 'upcoming'
  const returnTo = location.state?.returnTo
  const backTarget = getChairpersonDriveBackTarget({
    returnTo,
    fromTab,
    driveId,
  })

  const [activeTab, setActiveTab] = useState(0)
  const [driveDate, setDriveDate] = useState(location.state?.driveDate ?? null)
  const [accessError, setAccessError] = useState(null)

  useEffect(() => {
    if (!driveId || !accountId) return

    let active = true
    const loadContext = async () => {
      try {
        const response =
          await chairpersonService.getDriveDonorsEmailComposeContext(
            driveId,
            accountId
          )
        if (!active) return
        const drive = response?.data?.drive
        if (drive?.date && !driveDate) {
          setDriveDate(drive.date)
        }
        setAccessError(null)
      } catch (error) {
        if (!active) return
        setAccessError(error)
      }
    }

    loadContext()
    return () => {
      active = false
    }
  }, [driveId, accountId, driveDate])

  const driveHasOccurred = useMemo(() => {
    if (!driveDate) return fromTab === 'past'
    const driveDay = new Date(driveDate)
    const today = new Date()
    driveDay.setHours(0, 0, 0, 0)
    today.setHours(0, 0, 0, 0)
    return driveDay <= today
  }, [driveDate, fromTab])

  const handleBack = () => {
    navigate(
      backTarget.to,
      backTarget.state ? { state: backTarget.state } : undefined
    )
  }

  if (!accountId) {
    return (
      <Typography variant='body2' color='text.secondary'>
        Select an account to view drive donors.
      </Typography>
    )
  }

  if (accessError) {
    return (
      <Box>
        <CommonButton
          variant='text'
          startIcon={<ArrowBackIosNewOutlinedIcon sx={{ fontSize: 14 }} />}
          onClick={handleBack}
        >
          {backTarget.label}
        </CommonButton>
        <Typography variant='body1' color='error' sx={{ mt: 2 }}>
          {accessError?.response?.status === 403
            ? "You don't have access to this drive's donors."
            : 'Unable to load drive donors.'}
        </Typography>
      </Box>
    )
  }

  const tabs = [
    {
      label: 'Account Donors',
      content: <AccountDonorsTab driveId={driveId} accountId={accountId} />,
    },
    {
      label: 'Scheduled Appointments',
      content: (
        <ScheduledAppointmentsTab driveId={driveId} accountId={accountId} />
      ),
    },
    {
      label: 'Drive Attendees',
      content: (
        <DriveAttendeesTab
          driveId={driveId}
          accountId={accountId}
          driveHasOccurred={driveHasOccurred}
        />
      ),
    },
  ]

  return (
    <Box>
      <CommonButton
        variant='text'
        startIcon={<ArrowBackIosNewOutlinedIcon sx={{ fontSize: 14 }} />}
        onClick={handleBack}
        sx={{ mb: 2 }}
      >
        {backTarget.label}
      </CommonButton>

      <Typography variant='h5' fontWeight={700} sx={{ mb: 0.5 }}>
        Donors
      </Typography>
      {driveName ? (
        <Typography variant='body1' color='text.secondary' sx={{ mb: 0.5 }}>
          {driveName}
        </Typography>
      ) : null}
      {driveDate ? (
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Drive date: {formatIsoDateWithWeekday(driveDate)}
        </Typography>
      ) : (
        <Box sx={{ mb: 2 }} />
      )}

      <CommonTabs
        id='chairperson-drive-donors'
        value={activeTab}
        onChange={(e, value) => setActiveTab(value)}
        tabs={tabs}
      />
    </Box>
  )
}
